import { rem } from 'polished'
import styled from 'styled-components'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import Pill from 'LuxKit/Pill'
import config from 'constants/config'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import { Experiments } from 'constants/optimizely'
import useExperiment from 'hooks/Optimizely/useExperiment'
import FormatCurrency from 'components/Common/FormatCurrency'

import FlashHotelValueComparisonHorizontalBar from './FlashHotelValueComparisonHorizontalBar'
import FlashHotelValueComparisonVerticalBars from './FlashHotelValueComparisonVerticalBars'

const ComparisonCard = styled.div`
  padding: ${rem(20)};
  border-radius: ${props => props.theme.borderRadius.L};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

interface Props {
  price: number
  value: number
  discountPercent: number
  className?: string
}

function FlashHotelValueComparison(props: Props) {
  const { price, value, discountPercent, className } = props
  const intl = useIntl()
  const brandName = config.brandConfig.name
  const displayDiscountPercent = Math.floor(Math.min(100, Math.max(0, discountPercent)))
  const canShowComparison = price > 0 && value > price && displayDiscountPercent > 0
  const pricePercentage = canShowComparison ? Math.min(100, Math.max(0, (price / value) * 100)) : 0
  const valuePercentage = 100 - pricePercentage
  const displayPricePercentage = 100 - displayDiscountPercent

  const { variation } = useExperiment(
    Experiments.croRelativePricingComparison,
    { canActivate: canShowComparison },
  )

  const valueLabel = intl.formatMessage({
    id: 'RFalxf33ApL2r_Ogz25_',
    defaultMessage: 'Valued up to',
    description: 'Label for the full market value endpoint on the flash hotel comparison bar',
  })
  const pricePercentageLabel = intl.formatNumber(displayPricePercentage / 100, {
    style: 'percent',
    maximumFractionDigits: 0,
  })

  const ComparisonGraph = useMemo(() => {
    if (variation === 'variant_a') {
      return (
        <FlashHotelValueComparisonHorizontalBar
          brandName={brandName}
          price={price}
          value={value}
          pricePercentage={pricePercentage}
          valuePercentage={valuePercentage}
          valueLabel={valueLabel}
        />
      )
    }

    if (variation === 'variant_b') {
      return (
        <FlashHotelValueComparisonVerticalBars
          price={price}
          pricePercentage={displayPricePercentage}
          pricePercentageLabel={pricePercentageLabel}
        />
      )
    }

    return undefined
  }, [
    brandName,
    displayPricePercentage,
    price,
    pricePercentage,
    pricePercentageLabel,
    value,
    valueLabel,
    valuePercentage,
    variation,
  ])

  if (!canShowComparison || !ComparisonGraph) {
    return null
  }

  return (
    <ComparisonCard className={className}>
      <Group
        direction="vertical"
        tabletDirection="horizontal"
        gap={16}
        tabletGap={20}
        tabletVerticalAlign="center"
        fullWidth
      >
        <Group direction="vertical" gap={8}>
          <div>
            <Pill kind="primary" variant="success">
              {intl.formatMessage(
                {
                  id: 'oNPWhbigZZPC-1E8hnRj',
                  defaultMessage: 'Save up to {percent}%',
                  description: 'Discount percentage badge on offer pricing tile component',
                },
                { percent: displayDiscountPercent },
              )}
            </Pill>
          </div>
          <Group direction="vertical" gap={2}>
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage(
                {
                  id: 'BZZ9vzaiqD-mss3W-RAb',
                  defaultMessage: '{brandName} from {price}',
                  description: 'Heading on the flash hotel value comparison card showing the brand price',
                },
                {
                  brandName,
                  price: <FormatCurrency value={price} format="roundedDollar" />,
                },
              )}
            </BodyText>
            <BodyText variant="medium" colour="neutral-two">
              {intl.formatMessage(
                {
                  id: 'rD6qUTRYlLsTwy9sLZ_b',
                  defaultMessage: 'A complete package valued at up to {value}. Accommodation and special inclusions, all in one price.',
                  description: 'Body copy explaining the package value comparison for flash hotel offers',
                },
                { value: <FormatCurrency value={value} format="roundedDollar" /> },
              )}
            </BodyText>
          </Group>
        </Group>
        {ComparisonGraph}
      </Group>
    </ComparisonCard>
  )
}

export default FlashHotelValueComparison
